import { Locale as VantLocale } from 'vant';
import enUS from 'vant/es/locale/lang/en-US';
import zhCN from 'vant/es/locale/lang/zh-CN';
import { getQueryParam } from '@/common/util/getUrlParam';
import { messages, type SupportedLocale } from './messages';

export const DEFAULT_LOCALE: SupportedLocale = 'zh-CN';

const LANGUAGE_ALIASES: Record<string, SupportedLocale> = {
    // 中文（简体）
    // 简体中文 变体
    'zh': 'zh-CN',
    'zh-cn': 'zh-CN',
    'zh_cn': 'zh-CN',
    'zh-hans': 'zh-CN',
    'zh_hans': 'zh-CN',
    'zh-sg': 'zh-CN',

    // 繁体中文 变体
    'zh-tw': 'zh-TW',
    'zh_tw': 'zh-TW',
    'zh-hant': 'zh-TW',
    'zh_hant': 'zh-TW',
    'zh-hk': 'zh-TW',
    'zh_mo': 'zh-TW',

    // 英语 变体（全部映射到 en-US）
    'en': 'en-US',
    'en_us': 'en-US',
    'en-us': 'en-US',
    'en_gb': 'en-US',
    'en-gb': 'en-US',
    'en_au': 'en-US',
    'en-au': 'en-US',
    'en-in': 'en-US',
    'en_ca': 'en-US',
    'en-ca': 'en-US',
    'en_in': 'en-US',

    // 日语
    'ja': 'ja',
    'ja-jp': 'ja',
    'jp': 'ja',

    // 韩语
    'ko': 'ko',
    'ko-kr': 'ko',
    'kr': 'ko',

    // 德语
    'de': 'de',
    'de-de': 'de',

    // 西班牙语
    'es': 'es',
    'es-es': 'es',
    'es-mx': 'es',

    // 印尼语
    'id': 'id',
    'id-id': 'id',

    // 马来语
    'ms': 'ms',
    'ms-my': 'ms',

    // 泰语
    'th': 'th',
    'th-th': 'th',
};
;

// Vant UI 组件库的语言包映射
// 注意：Vant 只支持部分语言，不支持的会 fallback 到英文或中文
const VANT_LOCALE_MAP: Record<SupportedLocale, Record<string, unknown>> = {
    'zh-CN': zhCN as Record<string, unknown>,
    'en-US': enUS as Record<string, unknown>,
    'zh-TW': zhCN as Record<string, unknown>, // Vant 没有繁体中文，使用简体中文 fallback
    ja: enUS as Record<string, unknown>, // Vant 没有日语，使用英文 fallback
    ko: enUS as Record<string, unknown>, // Vant 没有韩语，使用英文 fallback
    de: enUS as Record<string, unknown>, // Vant 没有德语，使用英文 fallback
    es: enUS as Record<string, unknown>, // Vant 没有西班牙语，使用英文 fallback
    id: enUS as Record<string, unknown>, // Vant 没有印尼语，使用英文 fallback
    ms: enUS as Record<string, unknown>, // Vant 没有马来语，使用英文 fallback
    th: enUS as Record<string, unknown>, // Vant 没有泰语，使用英文 fallback
};

let currentLocale: SupportedLocale = DEFAULT_LOCALE;
    console.log(currentLocale, 'currentLocale1');

export const normalizeLocale = (value?: string | null): SupportedLocale => {
    if (!value) return DEFAULT_LOCALE;
    const trimmed = value.trim();
    if (!trimmed) return DEFAULT_LOCALE;

    const normalized = LANGUAGE_ALIASES[trimmed.toLowerCase()];
    if (normalized) return normalized;
    
    // 检查是否是直接支持的语言代码
    const supportedLocales: SupportedLocale[] = [
        'zh-CN', 'en-US', 'ja', 'ko', 'de', 'es', 'zh-TW', 'id', 'ms', 'th'
    ];
    if (supportedLocales.includes(trimmed as SupportedLocale)) {
        return trimmed as SupportedLocale;
    }
    
    return DEFAULT_LOCALE;
};

export const getLocaleFromUrl = () => {
    const languageFromUrl = decodeURIComponent(getQueryParam('language') || '');
    console.log(languageFromUrl, 'languageFromUrl');
    return normalizeLocale(languageFromUrl);
};

export const setLocale = (localeValue?: string | null) => {
    currentLocale = normalizeLocale(localeValue);
    console.log(currentLocale, 'currentLocale');
    
    const vantLocale = VANT_LOCALE_MAP[currentLocale] || zhCN;
    console.log(vantLocale, 'vantLocale');
    VantLocale.use(currentLocale, vantLocale);
    
    return currentLocale;
};

export const setupAppLocale = (localeValue?: string | null) => {
    return setLocale(localeValue);
};

export const t = <K extends keyof (typeof messages)[SupportedLocale]>(
    key: K,
): (typeof messages)[SupportedLocale][K] => {
    const table = messages[currentLocale] || messages[DEFAULT_LOCALE];
    return table[key];
};

// 获取当前语言
export const getCurrentLocale = (): SupportedLocale => {
    return currentLocale;
};

// 获取支持的语言列表
export const getSupportedLocales = (): { code: SupportedLocale; name: string }[] => {
    return [
        { code: 'zh-CN', name: '简体中文' },
        { code: 'zh-TW', name: '繁體中文' },
        { code: 'en-US', name: 'English' },
        { code: 'ja', name: '日本語' },
        { code: 'ko', name: '한국어' },
        { code: 'de', name: 'Deutsch' },
        { code: 'es', name: 'Español' },
        { code: 'id', name: 'Bahasa Indonesia' },
        { code: 'ms', name: 'Bahasa Melayu' },
        { code: 'th', name: 'ภาษาไทย' },
    ];
};